/**
 * CompanyPricingPresetsPage — list + create + archive saved pricing
 * strategies for company containers (NOT generic shipments — these
 * are the per-route pricing templates the organizer uses when
 * creating new containers).
 *
 *   GET    /api/cargo/me/company/pricing-presets
 *   POST   /api/cargo/me/company/pricing-presets
 *   DELETE /api/cargo/me/company/pricing-presets/:id
 *
 * Schema source-of-truth: server/middleware/validations + DAL.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, DollarSign, Plus, Loader, Trash2, Globe,
} from 'lucide-react';
import {
  useMyCompany,
  usePricingPresets,
  useCreatePricingPreset,
  useArchivePricingPreset,
} from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import { ConfirmDialog } from '@/components/common/Dialog';
import { CONTAINER_COUNTRIES } from '@/constants/countries';
import type {
  CreatePricingPresetInput, PricingPreset, PricingPresetMode,
} from '@/types/companyExtras';

const FIELD_CLASSES =
  'w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none transition focus:border-primary';

const PRICING_MODE_LABEL: Record<PricingPresetMode, string> = {
  per_kg: 'بالوزن',
  per_cbm: 'بالحجم',
  mixed: 'مختلط',
};

const CONTAINER_TYPE_OPTIONS = [
  { value: '20ft', label: '20ft' },
  { value: '40ft', label: '40ft' },
  { value: '40hc', label: '40HC' },
  { value: 'lcl',  label: 'LCL' },
  { value: 'air',  label: 'جوي' },
];

export default function CompanyPricingPresetsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: company, isLoading: companyLoading } = useMyCompany();
  const { data: presets, isLoading } = usePricingPresets();
  const create = useCreatePricingPreset();
  const [showForm, setShowForm] = useState(false);

  if (companyLoading || isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!company) {
    navigate('/company', { replace: true });
    return null;
  }

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
              <DollarSign size={20} /> {t('pricing_presets_title', 'استراتيجيات التسعير')}
            </h1>
            <p className="text-text-secondary text-sm">
              {t('pricing_presets_subtitle', 'احفظ أسعارك المعتادة لكل خط شحن لتسريع إنشاء الحاويات.')}
            </p>
          </div>
          {!showForm && (
            <Button variant="primary" onClick={() => setShowForm(true)}>
              <Plus size={18} className="ml-1" />
              {t('add', 'إضافة')}
            </Button>
          )}
        </div>

        {showForm && (
          <PresetForm
            loading={create.isPending}
            onCancel={() => setShowForm(false)}
            onSubmit={async (values) => {
              await create.mutateAsync(values);
              setShowForm(false);
            }}
          />
        )}

        {(!presets || presets.length === 0) && !showForm ? (
          <div className="bg-surface border border-border rounded-2xl p-10 text-center">
            <DollarSign size={48} className="mx-auto text-text-tertiary mb-3" />
            <p className="text-text-secondary">
              {t('pricing_presets_empty', 'لم تُضف أيّ استراتيجية بعد. اضغط "إضافة" للبدء.')}
            </p>
          </div>
        ) : (
          <div className="space-y-3 mt-3">
            {presets?.map((p) => <PresetCard key={p.id} preset={p} />)}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function PresetCard({ preset }: { preset: PricingPreset }) {
  const { t } = useTranslation();
  const archive = useArchivePricingPreset();
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const route = (preset.origin_country || preset.destination_country)
    ? `${preset.origin_country?.toUpperCase() || '?'} → ${preset.destination_country?.toUpperCase() || '?'}`
    : null;

  return (
    <>
      <div className="bg-surface border border-border rounded-2xl p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-text-primary mb-1">{preset.name}</h3>
            {preset.description && (
              <p className="text-sm text-text-secondary mb-2">{preset.description}</p>
            )}
            <div className="flex flex-wrap gap-3 text-xs text-text-tertiary mb-2">
              {route && (
                <span className="flex items-center gap-1">
                  <Globe size={11} />
                  <span dir="ltr">{route}</span>
                </span>
              )}
              {preset.container_type && (
                <span className="px-2 py-0.5 bg-border/40 rounded font-bold">
                  {preset.container_type.toUpperCase()}
                </span>
              )}
              <span className="px-2 py-0.5 bg-primary/10 text-primary rounded font-bold">
                {PRICING_MODE_LABEL[preset.pricing_mode]}
              </span>
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              {preset.price_per_kg_usd != null && (
                <span>
                  <span className="text-text-tertiary">{t('price_per_kg_usd', 'السعر/kg')}:</span>{' '}
                  <strong>${preset.price_per_kg_usd}</strong>
                </span>
              )}
              {preset.price_per_cbm_usd != null && (
                <span>
                  <span className="text-text-tertiary">{t('price_per_cbm_usd', 'السعر/m³')}:</span>{' '}
                  <strong>${preset.price_per_cbm_usd}</strong>
                </span>
              )}
              {preset.minimum_charge_usd != null && preset.minimum_charge_usd > 0 && (
                <span>
                  <span className="text-text-tertiary">{t('min_charge', 'حد أدنى')}:</span>{' '}
                  <strong>${preset.minimum_charge_usd}</strong>
                </span>
              )}
              {preset.clearance_fee_usd != null && preset.clearance_fee_usd > 0 && (
                <span>
                  <span className="text-text-tertiary">{t('clearance_fee', 'تخليص')}:</span>{' '}
                  <strong>${preset.clearance_fee_usd}</strong>
                </span>
              )}
              {preset.insurance_rate_bps != null && (
                <span>
                  <span className="text-text-tertiary">{t('insurance', 'تأمين')}:</span>{' '}
                  <strong>{(preset.insurance_rate_bps / 100).toFixed(2)}%</strong>
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => { setError(null); setShowConfirm(true); }}
            className="text-error/70 hover:text-error p-2 rounded-lg hover:bg-error/5"
            aria-label="archive"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={showConfirm}
        title={t('preset_archive_title', 'إخفاء الاستراتيجية')}
        body={t('preset_archive_confirm', 'إخفاء هذه الاستراتيجية؟ لن تظهر في القوائم بعد ذلك.')}
        destructive
        loading={archive.isPending}
        errorMessage={error}
        onCancel={() => setShowConfirm(false)}
        onConfirm={async () => {
          setError(null);
          try {
            await archive.mutateAsync(preset.id);
            setShowConfirm(false);
          } catch (e: any) {
            setError(e?.message || 'فشل الإخفاء');
          }
        }}
      />
    </>
  );
}

function PresetForm({
  loading, onSubmit, onCancel,
}: {
  loading: boolean;
  onSubmit: (v: CreatePricingPresetInput) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [v, setV] = useState<CreatePricingPresetInput>({
    name: '',
    pricingMode: 'per_kg',
    insuranceRateBps: 150,
  });

  return (
    <form
      className="bg-surface border border-border rounded-2xl p-5 mb-3 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!v.name.trim()) return;
        onSubmit(v);
      }}
    >
      <TextField
        label={t('preset_name', 'اسم الاستراتيجية') + ' *'}
        value={v.name}
        onChange={(e) => setV({ ...v, name: e.target.value })}
        placeholder={t('preset_name_ph', 'مثال: الصين-اللاذقية اقتصادي')}
        required
      />

      <Field label={t('description', 'الوصف')}>
        <textarea
          value={v.description || ''}
          onChange={(e) => setV({ ...v, description: e.target.value })}
          className={FIELD_CLASSES + ' min-h-[60px]'}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={t('origin_country', 'بلد المنشأ')}>
          <select
            value={v.originCountry || ''}
            onChange={(e) => setV({ ...v, originCountry: e.target.value || undefined })}
            className={FIELD_CLASSES}
          >
            <option value="">—</option>
            {CONTAINER_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </Field>
        <Field label={t('destination_country', 'بلد الوجهة')}>
          <select
            value={v.destinationCountry || ''}
            onChange={(e) => setV({ ...v, destinationCountry: e.target.value || undefined })}
            className={FIELD_CLASSES}
          >
            <option value="">—</option>
            {CONTAINER_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </Field>
      </div>

      <Field label={t('container_type', 'نوع الحاوية')}>
        <select
          value={v.containerType || ''}
          onChange={(e) => setV({ ...v, containerType: e.target.value || undefined })}
          className={FIELD_CLASSES}
        >
          <option value="">—</option>
          {CONTAINER_TYPE_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </Field>

      <Field label={t('pricing_mode_label', 'نمط التسعير') + ' *'}>
        <select
          value={v.pricingMode}
          onChange={(e) => setV({ ...v, pricingMode: e.target.value as PricingPresetMode })}
          className={FIELD_CLASSES}
          required
        >
          <option value="per_kg">بالوزن (per_kg)</option>
          <option value="per_cbm">بالحجم (per_cbm)</option>
          <option value="mixed">مختلط (mixed)</option>
        </select>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        {(v.pricingMode === 'per_kg' || v.pricingMode === 'mixed') && (
          <TextField
            label={t('price_per_kg_usd', 'السعر / kg (USD)') + ' *'}
            type="number"
            value={v.pricePerKgUsd ?? ''}
            onChange={(e) => setV({ ...v, pricePerKgUsd: e.target.value === '' ? undefined : Number(e.target.value) })}
            min={0} step={0.01} dir="ltr"
          />
        )}
        {(v.pricingMode === 'per_cbm' || v.pricingMode === 'mixed') && (
          <TextField
            label={t('price_per_cbm_usd', 'السعر / m³ (USD)') + ' *'}
            type="number"
            value={v.pricePerCbmUsd ?? ''}
            onChange={(e) => setV({ ...v, pricePerCbmUsd: e.target.value === '' ? undefined : Number(e.target.value) })}
            min={0} step={0.01} dir="ltr"
          />
        )}
      </div>

      {v.pricingMode === 'mixed' && (
        <Field label={t('mixed_rule', 'قاعدة المختلط') + ' *'}>
          <select
            value={v.pricingMixedRule || ''}
            onChange={(e) => setV({ ...v, pricingMixedRule: e.target.value as 'max' | 'sum' })}
            className={FIELD_CLASSES} required
          >
            <option value="">—</option>
            <option value="max">الأكبر بين الاثنين</option>
            <option value="sum">المجموع</option>
          </select>
        </Field>
      )}

      <div className="grid grid-cols-3 gap-3">
        <TextField
          label={t('min_charge_usd', 'حد أدنى (USD)')}
          type="number"
          value={v.minimumChargeUsd ?? 0}
          onChange={(e) => setV({ ...v, minimumChargeUsd: Number(e.target.value) })}
          min={0} dir="ltr"
        />
        <TextField
          label={t('clearance_fee_usd', 'تخليص (USD)')}
          type="number"
          value={v.clearanceFeeUsd ?? 0}
          onChange={(e) => setV({ ...v, clearanceFeeUsd: Number(e.target.value) })}
          min={0} dir="ltr"
        />
        <TextField
          label={t('insurance_rate_bps', 'تأمين (bps)')}
          type="number"
          value={v.insuranceRateBps ?? 150}
          onChange={(e) => setV({ ...v, insuranceRateBps: Number(e.target.value) })}
          min={0} max={10000} dir="ltr"
        />
      </div>

      <div className="flex gap-2 pt-1">
        <Button variant="primary" loading={loading} type="submit">
          {t('save', 'حفظ')}
        </Button>
        <Button variant="ghost" type="button" onClick={onCancel}>
          {t('cancel', 'إلغاء')}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-text-secondary mb-1 block">{label}</span>
      {children}
    </label>
  );
}
